import React from "react";
import styled from "@emotion/styled";
import { Pill } from "../Pill";
import { Banner, BannerTypes } from "../Banner";
import { UnderlinedButtonStyle } from "../../components/ContactCard/shared";

import { WARNING_20 } from "../../design-tokens";
import type { Contact } from "./types";

const CreditStatus = ({
  creditStatus,
  contact,
  text,
  onRunCreditCheck,
  onRefreshCreditCheck,
}: CreditStatusProps) => {
  const { isFailed, isPassed, isInProgress } = creditStatus;
  const {
    approved = "Monthly credit approved",
    notApproved = "Monthly credit not approved",
    notChecked = "Monthly credit not checked",
    refreshCredit = "Refresh credit check",
    runCredit = "Run credit check",
  } = text;

  switch (true) {
    case isInProgress:
      return <Pill text={notApproved} type="error" />;
    case isPassed:
      return <Pill text={approved} type="success" />;
    case isFailed:
      if (!onRunCreditCheck) {
        return <Pill text={notApproved} type="error" />;
      }

      return (
        <Banner
          bannerTestId="in-progress-banner"
          style={{ position: "relative" }}
          bannerType={BannerTypes.InfoLight}
          showClose={false}
          show
          customButtons={
            <UnderlinedButtonStyle
              customTestId="refresh-credit-check-button"
              color="tertiary"
              onClick={onRefreshCreditCheck}
            >
              {refreshCredit}
            </UnderlinedButtonStyle>
          }
        >
          {notApproved}
        </Banner>
      );
    default:
      if (!onRunCreditCheck) {
        return <Pill text={notChecked} type="warning" />;
      }

      return (
        <Banner
          bannerTestId="not-checked-banner"
          style={{ position: "relative" }}
          bannerType={BannerTypes.WarningLight}
          showClose={false}
          show
          customButtons={
            <CreditCheckButton
              customTestId="run-credit-check-button"
              color="tertiary"
              onClick={() => onRunCreditCheck(contact)}
            >
              {runCredit}
            </CreditCheckButton>
          }
        >
          {notChecked}
        </Banner>
      );
  }
};

type CreditStatusProps = {
  creditStatus: Contact["creditStatus"];
  text?: {
    approved?: string;
    notApproved?: string;
    notChecked?: string;
    runCredit?: string;
    refreshCredit?: string;
  };
  onRunCreditCheck?: (selectedContact: Contact) => void;
  onRefreshCreditCheck?: () => void;
  contact: Contact;
};

const CreditCheckButton = styled(UnderlinedButtonStyle)`
  color: ${WARNING_20};
`;

export type { CreditStatusProps };
export { CreditStatus };
